import type { ComponentType } from "react";
import type { RouteObject } from "react-router";
import type { WorkspaceRole } from "../lib/auth-api";
import type { IconName } from "../ui/Icon";

/**
 * Which chrome a route renders inside (docs/spec/routes.md "Layouts").
 * - `shell`: top bar + app sidebar (every logged-in page)
 * - `settings`: top bar + workspace-settings sub-header, no app sidebar (logged in)
 * - `auth`: centred card. Guest-only unless the route sets `handle: { guestOnly: false }`
 * - `public`: static pages (privacy, terms, unsubscribe)
 * - `shared-report`, `kiosk`: their own full-screen frames
 * - `handoff`: bare page with no chrome (extension login hand-off)
 */
export const LAYOUT_KEYS = ["shell", "settings", "auth", "public", "shared-report", "kiosk", "handoff"] as const;
export type LayoutKey = (typeof LAYOUT_KEYS)[number];

/** Route `handle` keys the shell reads. */
export interface RouteHandle {
  /** Rendered as `<title> • Clockify`. */
  title?: string;
  /** `auth` layout only: false lets signed-in users through (reset password, invitations). */
  guestOnly?: boolean;
}

/** The shell's sidebar ids, in the live default order (routes.md section 2). */
export const SIDEBAR_IDS = [
  "TIMESHEET",
  "CALENDAR",
  "TIME_TRACKER",
  "DASHBOARD",
  "REPORTS",
  "PROJECTS",
  "TEAM",
  "CLIENTS",
  "TAGS",
  "KIOSK",
  "SCHEDULE",
  "EXPENSES",
  "TIME_OFF",
  "ACTIVITY",
  "APPROVALS",
  "INVOICES",
  "ACTIVITY_MONITORING",
] as const;
export type SidebarId = (typeof SIDEBAR_IDS)[number];

export type SidebarSection = "MAIN" | "ANALYZE" | "MANAGE" | "SHOW_MORE";

/** What a sidebar `visible` rule may look at. */
export interface ShellContext {
  role: WorkspaceRole | null;
  isAdmin: boolean;
  workspaceSettings: Record<string, unknown>;
  features: string[];
}

export interface FlyoutGroup {
  header: string;
  items: Array<{ label: string; path: string }>;
}

export interface SidebarEntry {
  id: SidebarId;
  /** Mixed case like Clockify's DOM; CSS uppercases it. */
  label: string;
  /** Sidebar href, hash included (e.g. `/time-off#requests`). */
  path: string;
  icon: IconName;
  /** Feature flag + role rule (routes.md section 2). Omitted means always visible. */
  visible?: (ctx: ShellContext) => boolean;
  /** REPORTS only. */
  flyout?: FlyoutGroup[];
  /** Path prefixes that mark the item active; defaults to the pathname of `path`. */
  activePrefixes?: string[];
  /** Screen-reader name, e.g. `View Time tracker`. */
  alt?: string;
}

export interface SettingsSection {
  /** Hash without `#`, e.g. `time-tracking`. */
  hash: string;
  label: string;
  group: 1 | 2 | 3;
  order: number;
  Component: ComponentType;
}

export interface AppModule {
  /** Module key from build-plan (kebab-case). */
  name: string;
  routes: Partial<Record<LayoutKey, RouteObject[]>>;
  /** Entries for the shell's ids; the shell owns order, sections and SHOW MORE. */
  sidebar?: SidebarEntry[];
  /** Workspace-settings hash sections (settings vertical); a matching `hash` replaces the placeholder. */
  settingsSections?: SettingsSection[];
}

/** Name used in the shell brief; same type. */
export type ModuleDefinition = AppModule;
